import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';

import { useDocumentUploadStepContext } from '../documentUpload/context/documentUploadStep.context';
import { usePersistUploadedDocuments } from '../documentUpload/hooks/usePersistUploadedDocuments';
import {
  PresentationStepContext,
  PresentationStepContextValue,
} from './PresentationTemplateSelectionStep.context';

interface PresentationStepProviderProps {
  value: PresentationStepContextValue;
}

export function PresentationStepProvider({
  children,
  value,
}: PropsWithChildren<PresentationStepProviderProps>) {
  const { householdId, hasBeenClosed, markCommitted } =
    useDocumentUploadStepContext();
  const trackUserEvent = useTrackUserEvent();
  const { whenUploadsSettle } = useMultiDocumentUploaderContext();
  const { persist } = usePersistUploadedDocuments();
  const [isPersistingUploads, setIsPersistingUploads] = useState(false);

  // The uploaded-file set can grow while uploads settle, so call the latest
  // persist callback after the wait instead of the callback captured on click.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  const completeOnboarding = useCallback(
    async (templateIds: string[]) => {
      // Record the skip even if uploads or onboarding completion fail.
      if (templateIds.length === 0) {
        trackUserEvent('client_onboarding presentation skip clicked', {
          householdId,
        });
      }
      markCommitted();
      setIsPersistingUploads(true);
      try {
        await whenUploadsSettle();
        const documentIds = await persistRef.current();
        if (hasBeenClosed() || !documentIds) return;
        await value.completeOnboarding(templateIds);
      } finally {
        setIsPersistingUploads(false);
      }
    },
    [
      hasBeenClosed,
      householdId,
      markCommitted,
      trackUserEvent,
      value,
      whenUploadsSettle,
    ]
  );

  const contextValue = useMemo<PresentationStepContextValue>(
    () => ({
      ...value,
      completeOnboarding,
      isCompleting: value.isCompleting || isPersistingUploads,
    }),
    [completeOnboarding, isPersistingUploads, value]
  );

  return (
    <PresentationStepContext.Provider value={contextValue}>
      {children}
    </PresentationStepContext.Provider>
  );
}
